if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          desktopPrevious: this.querySelector('[data-inewer-gallery-previous]'),
          desktopNext: this.querySelector('[data-inewer-gallery-next]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopMql = window.matchMedia('(min-width: 990px)');
        this.tabletMql = window.matchMedia('(min-width: 750px) and (max-width: 989px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailButtons();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        this.elements.desktopPrevious?.addEventListener('click', () => this.changeActiveMedia(-1));
        this.elements.desktopNext?.addEventListener('click', () => this.changeActiveMedia(1));

        this.elements.viewer.querySelectorAll('.slider-buttons .slider-button').forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              if (!this.mql.matches) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.changeActiveMedia(button.name === 'previous' ? -1 : 1);
            },
            true
          );
        });

        this.elements.thumbnails.querySelectorAll(':scope > .slider-button').forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              if (!this.desktopMql.matches) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.scrollThumbnails(button.name === 'previous' ? -1 : 1);
            },
            true
          );
        });

        this.onThumbnailResize = () => this.syncThumbnailHeight();
        this.thumbnailResizeObserver = new ResizeObserver(() => this.syncThumbnailHeight());
        this.bindTabletSwipe();
      }

      connectedCallback() {
        this.setupTabletSticky();
        if (!this.thumbnailResizeObserver || this.galleryObserved) return;
        this.galleryObserved = true;
        this.desktopMql.addEventListener('change', this.onThumbnailResize);
        this.thumbnailResizeObserver.observe(this.elements.viewer);
        this.syncThumbnailHeight();
      }

      disconnectedCallback() {
        this.teardownTabletSticky();
        this.galleryObserved = false;
        this.desktopMql.removeEventListener('change', this.onThumbnailResize);
        this.thumbnailResizeObserver?.disconnect();
        window.cancelAnimationFrame(this.thumbnailHeightFrame);
      }

      setupTabletSticky() {
        const productInfo = this.closest('product-info[id^="MainProduct-"]');
        const row = this.closest('.product');
        const wrapper = this.closest('.product__media-wrapper');
        const info = row?.querySelector(':scope > .product__info-wrapper');
        if (this.tabletStickyResizeObserver || !productInfo || productInfo.closest('quick-add-modal') ||
            !document.getElementById('MainContent')?.contains(productInfo) || !row || !wrapper || !info) return;

        this.tabletStickyRow = row;
        this.tabletStickyWrapper = wrapper;
        this.tabletStickyInfo = info;
        this.tabletStickyProduct = productInfo;
        this.tabletStickyHeader = document.querySelector('.section-header') || document.querySelector('sticky-header');
        this.onTabletStickyResize = () => this.scheduleTabletSticky();
        this.tabletStickyResizeObserver = new ResizeObserver(this.onTabletStickyResize);
        [this, row, info, this.tabletStickyHeader].filter(Boolean).forEach(element => {
          this.tabletStickyResizeObserver.observe(element);
        });
        window.addEventListener('resize', this.onTabletStickyResize, { passive: true });
        window.visualViewport?.addEventListener('resize', this.onTabletStickyResize, { passive: true });
        this.addEventListener('load', this.onTabletStickyResize, true);
        this.addEventListener('inewer:gallery-updated', this.onTabletStickyResize);
        this.scheduleTabletSticky();
      }

      clearTabletSticky() {
        this.tabletStickyRow?.removeAttribute('data-inewer-tablet-gallery-active');
        this.tabletStickyRow?.removeAttribute('data-inewer-tablet-gallery-managed');
        this.style.removeProperty('--inewer-tablet-gallery-top');
      }

      teardownTabletSticky() {
        this.tabletStickyResizeObserver?.disconnect();
        this.tabletStickyResizeObserver = null;
        window.cancelAnimationFrame(this.tabletStickyFrame);
        window.removeEventListener('resize', this.onTabletStickyResize);
        window.visualViewport?.removeEventListener('resize', this.onTabletStickyResize);
        this.removeEventListener('load', this.onTabletStickyResize, true);
        this.removeEventListener('inewer:gallery-updated', this.onTabletStickyResize);
        this.clearTabletSticky();
      }

      scheduleTabletSticky() {
        window.cancelAnimationFrame(this.tabletStickyFrame);
        this.tabletStickyFrame = window.requestAnimationFrame(() => this.updateTabletSticky());
      }

      updateTabletSticky() {
        if (!this.isConnected || !this.tabletStickyRow) return;
        if (!window.matchMedia('(min-width: 750px) and (max-width: 1199px)').matches) {
          this.clearTabletSticky();
          return;
        }

        const row = this.tabletStickyRow;
        const mediaRect = this.tabletStickyWrapper.getBoundingClientRect();
        const infoRect = this.tabletStickyInfo.getBoundingClientRect();
        const galleryRect = this.getBoundingClientRect();
        const header = this.tabletStickyHeader;
        const headerHeight = header?.getBoundingClientRect().height || 0;
        const headerInset = header ? Math.max(0, parseFloat(getComputedStyle(header).top) || 0) : 0;
        const top = Math.ceil(headerHeight + headerInset + 8);
        const viewportHeight = Math.min(window.innerHeight, document.documentElement.clientHeight || window.innerHeight,
          window.visualViewport?.height || window.innerHeight);
        const availableHeight = viewportHeight - top - 12;
        const sideBySide = mediaRect.width > 0 && infoRect.width > 0 && mediaRect.right <= infoRect.left + 1;
        let safeAncestors = true;
        for (let ancestor = this.parentElement; ancestor; ancestor = ancestor.parentElement) {
          const style = getComputedStyle(ancestor);
          if (['hidden', 'clip', 'auto', 'scroll'].includes(style.overflowY) || style.transform !== 'none') {
            safeAncestors = false;
            break;
          }
          if (ancestor === this.tabletStickyProduct) break;
        }
        const eligible = sideBySide && safeAncestors && galleryRect.height > 0 &&
          galleryRect.height <= availableHeight && infoRect.height - galleryRect.height >= 80 &&
          (!window.visualViewport || Math.abs(window.visualViewport.scale - 1) < 0.01);

        row.toggleAttribute('data-inewer-tablet-gallery-managed', true);
        row.toggleAttribute('data-inewer-tablet-gallery-active', eligible);
        if (eligible) {
          const value = `${top}px`;
          if (this.style.getPropertyValue('--inewer-tablet-gallery-top') !== value) {
            this.style.setProperty('--inewer-tablet-gallery-top', value);
          }
        } else {
          this.style.removeProperty('--inewer-tablet-gallery-top');
        }
      }

      bindThumbnailButtons() {
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.galleryBound === 'true') return;
          button.dataset.galleryBound = 'true';
          button.addEventListener('click', () => {
            this.setActiveMedia(mediaToSwitch.dataset.target, false, { syncFeaturedThumbnail: false });
          });
        });
      }

      refreshMediaItems() {
        this.bindThumbnailButtons();
        if (!this.mql.matches) this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        this.inewerMediaRevision = (this.inewerMediaRevision || 0) + 1;
        this.dispatchEvent(new CustomEvent('inewer:gallery-updated', { bubbles: true }));
      }

      bindTabletSwipe() {
        let touchStart = null;
        this.elements.viewer.addEventListener(
          'touchstart',
          (event) => {
            if (!this.tabletMql.matches || event.touches.length !== 1) return;
            touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
          },
          { passive: true }
        );
        this.elements.viewer.addEventListener(
          'touchend',
          (event) => {
            if (!this.tabletMql.matches || !touchStart || event.changedTouches.length !== 1) return;
            const deltaX = event.changedTouches[0].clientX - touchStart.x;
            const deltaY = event.changedTouches[0].clientY - touchStart.y;
            touchStart = null;
            if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
            this.changeActiveMedia(deltaX < 0 ? 1 : -1);
          },
          { passive: true }
        );
        this.elements.viewer.addEventListener('touchcancel', () => {
          touchStart = null;
        });
      }

      changeActiveMedia(step) {
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')
        );
        if (mediaItems.length < 2) return;

        const currentIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const safeIndex = currentIndex < 0 ? 0 : currentIndex;
        const nextIndex = (safeIndex + step + mediaItems.length) % mediaItems.length;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false, { syncFeaturedThumbnail: false });
      }

      scrollThumbnails(direction) {
        const slider = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!slider) return;
        slider.scrollBy({ top: direction * Math.max(slider.clientHeight * 0.72, 220), behavior: 'smooth' });
      }

      syncThumbnailHeight() {
        window.cancelAnimationFrame(this.thumbnailHeightFrame);
        if (!this.desktopMql.matches) {
          this.style.removeProperty('--inewer-gallery-viewer-height');
          return;
        }

        this.thumbnailHeightFrame = window.requestAnimationFrame(() => {
          if (!this.isConnected) return;
          const viewerHeight = this.elements.viewer.getBoundingClientRect().height;
          if (viewerHeight > 0) this.style.setProperty('--inewer-gallery-viewer-height', `${viewerHeight}px`);
        });
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (!mediaId) return;
        this.setActiveMedia(mediaId, false, { syncFeaturedThumbnail: false, skipScroll: true });
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        activeMedia?.classList?.remove('scroll-trigger--offscreen');

        if (this.mql.matches) {
          const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
          const activeIndex = mediaItems.indexOf(activeMedia);
          const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
          const totalCounter = this.elements.viewer.querySelector('.slider-counter--total');
          if (currentCounter && activeIndex >= 0) currentCounter.textContent = String(activeIndex + 1);
          if (totalCounter) totalCounter.textContent = String(mediaItems.length);
        }

        let activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        const firstThumbnail = this.elements.thumbnails?.querySelector('.thumbnail-list__item');
        const activeImageAlt = activeMedia.querySelector('img')?.alt || '';
        const isVariantFrontImage = / - Front(?: View)?$/i.test(activeImageAlt);

        if (
          options.syncFeaturedThumbnail !== false &&
          isVariantFrontImage &&
          firstThumbnail &&
          activeThumbnail !== firstThumbnail
        ) {
          activeThumbnail = this.syncFeaturedThumbnail(activeMedia, mediaId);
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!options.skipScroll && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // On mobile, changing a color should only move the horizontal
          // gallery to the selected image. Never reposition the whole page.
          if (!this.mql.matches) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails || !activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      syncFeaturedThumbnail(activeMedia, mediaId) {
        const featuredThumbnail = this.elements.thumbnails?.querySelector('.thumbnail-list__item');
        const sourceImage = activeMedia.querySelector('.product__modal-opener--image img, img');
        const thumbnailImage = featuredThumbnail?.querySelector('img');

        if (!featuredThumbnail || !sourceImage || !thumbnailImage) return null;

        const productInfo = this.closest('product-info');
        const galleryState = productInfo?.inewerGalleryState;
        const thumbnailList = this.querySelector('.thumbnail-list');
        const knownThumbnails = galleryState?.gallery === this &&
          galleryState.liveThumbnails !== null &&
          galleryState.liveThumbnails === productInfo.mediaListState(thumbnailList);

        const sourceSrc = sourceImage.getAttribute('src');
        const sourceSrcset = sourceImage.getAttribute('srcset');

        if (sourceSrc) thumbnailImage.setAttribute('src', sourceSrc);
        if (sourceSrcset) thumbnailImage.setAttribute('srcset', sourceSrcset);
        else thumbnailImage.removeAttribute('srcset');

        thumbnailImage.alt = sourceImage.alt || '';
        featuredThumbnail.dataset.target = mediaId;
        // Preserve only this known thumbnail synchronization, never unrelated changes.
        if (knownThumbnails) galleryState.liveThumbnails = productInfo.mediaListState(thumbnailList);
        return featuredThumbnail;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // The theme intentionally hides variant front-view thumbnails with an
        // !important CSS rule. Once that media becomes the gallery's source of
        // truth it must participate in the thumbnail slider layout, otherwise
        // it has a 0x0 rect and no scrolling API can bring it into view.
        if (window.getComputedStyle(thumbnail).display === 'none') {
          thumbnail.style.setProperty('display', 'list-item', 'important');
          thumbnail.dataset.galleryForcedVisible = 'true';
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // A variant update changes the slider's measurable children. Refresh
        // its existing page state before checking/setting the scroll position.
        this.elements.thumbnails.resetPages?.();

        if (this.desktopMql.matches) {
          const slider = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('.thumbnail-list');
          if (!slider) return;

          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = slider.scrollTop;
          const visibleBottom = visibleTop + slider.clientHeight;

          if (thumbnailTop < visibleTop || thumbnailBottom > visibleBottom) {
            slider.scrollTo({
              top: Math.max(0, thumbnailTop - (slider.clientHeight - thumbnail.offsetHeight) / 2),
              behavior: 'smooth',
            });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'auto' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}





